if (!customElements.get('announcement-countdown')) {
  customElements.define(
    'announcement-countdown',
    class AnnouncementCountdown extends HTMLElement {
      connectedCallback() {
        this.endDate = this.parseEndDate();
        if (!this.endDate) {
          this.hidden = true;
          return;
        }
        this.format = this.dataset.format || 'dhms';
        this.showLabels = this.dataset.showLabels !== 'false';
        this.expiredBehavior = this.dataset.expiredBehavior || 'hide_slide';
        this.expiredMessage = this.dataset.expiredMessage || 'Offer ended';
        this.renderStructure();
        this.tick();
        this.intervalId = window.setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        if (this.intervalId) {
          window.clearInterval(this.intervalId);
        }
      }

      getUnitsForFormat() {
        switch (this.format) {
          case 'hms':
            return [
              { key: 'hours', label: 'hours' },
              { key: 'minutes', label: 'mins' },
              { key: 'seconds', label: 'secs' },
            ];
          case 'dhm':
            return [
              { key: 'days', label: 'days' },
              { key: 'hours', label: 'hours' },
              { key: 'minutes', label: 'mins' },
            ];
          case 'dhms':
          default:
            return [
              { key: 'days', label: 'days' },
              { key: 'hours', label: 'hours' },
              { key: 'minutes', label: 'mins' },
              { key: 'seconds', label: 'secs' },
            ];
        }
      }

      renderStructure() {
        const units = this.getUnitsForFormat();
        const container = document.createElement('div');
        container.className = 'announcement-bar__timer-units';

        if (!this.showLabels) {
          container.classList.add('announcement-bar__timer-units--no-labels');
          this.classList.add('announcement-bar__timer--no-labels');
        }

        this.unitElements = {};

        units.forEach(({ key, label }, index) => {
          const unit = document.createElement('div');
          unit.className = 'announcement-bar__timer-unit';
          unit.dataset.unit = key;
          const value = document.createElement('span');
          value.className = 'announcement-bar__timer-value';
          value.textContent = '00';
          unit.appendChild(value);

          if (this.showLabels) {
            const labelEl = document.createElement('span');
            labelEl.className = 'announcement-bar__timer-label';
            labelEl.textContent = label;
            unit.appendChild(labelEl);
          }

          container.appendChild(unit);

          if (!this.showLabels && index < units.length - 1) {
            const separator = document.createElement('span');
            separator.className = 'announcement-bar__timer-separator';
            separator.textContent = ':';
            separator.setAttribute('aria-hidden', 'true');
            container.appendChild(separator);
          }
          this.unitElements[key] = value;
        });
        this.replaceChildren(container);
      }

      parseEndDate() {
        const endTs = this.dataset.endTs;
        if (endTs) {
          const timestamp = Number(endTs) * 1000;
          if (!Number.isNaN(timestamp) && timestamp > 0) {
            return new Date(timestamp);
          }
        }

        const timeZone = this.dataset.timezone || this.closest('[data-store-timezone]')?.dataset.storeTimezone;

        if (this.dataset.end && timeZone) {
          return this.parseDateTimeInTimeZone(this.dataset.end, timeZone);
        }

        return null;
      }

      parseDateTimeInTimeZone(value, timeZone) {
        const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})$/);
        if (!match) return null;

        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);
        const hour = Number(match[4]);
        const minute = Number(match[5]);
        const desiredUtc = Date.UTC(year, month - 1, day, hour, minute, 0);

        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone,
          hour12: false,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });

        let timestamp = desiredUtc;

        for (let attempt = 0; attempt < 5; attempt += 1) {
          const parts = Object.fromEntries(
            formatter.formatToParts(new Date(timestamp)).map((part) => [part.type, part.value]),
          );

          const formattedUtc = Date.UTC(
            Number(parts.year),
            Number(parts.month) - 1,
            Number(parts.day),
            Number(parts.hour),
            Number(parts.minute),
            Number(parts.second),
          );
          timestamp += desiredUtc - formattedUtc;
        }

        const result = new Date(timestamp);
        return Number.isNaN(result.getTime()) ? null : result;
      }

      tick() {
        const diff = this.endDate.getTime() - Date.now();

        if (diff <= 0) {
          this.handleExpired();
          return;
        }

        this.updateDisplay(this.getTimeValues(diff));
      }

      getTimeValues(diffMs) {
        const totalSeconds = Math.floor(diffMs / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const totalHours = Math.floor(totalSeconds / 3600);
        return { days, hours, minutes, seconds, totalHours };
      }

      updateDisplay(values) {
        const pad = (value) => String(value).padStart(2, '0');
        const ariaParts = [];
        if (this.unitElements.days) {
          this.unitElements.days.textContent = pad(values.days);
          ariaParts.push(`${values.days} days`);
        }

        if (this.unitElements.hours) {
          const hoursValue = this.format === 'hms' ? values.totalHours : values.hours;
          this.unitElements.hours.textContent = pad(hoursValue);
          ariaParts.push(`${hoursValue} hours`);
        }

        if (this.unitElements.minutes) {
          this.unitElements.minutes.textContent = pad(values.minutes);
          ariaParts.push(`${values.minutes} minutes`);
        }

        if (this.unitElements.seconds) {
          this.unitElements.seconds.textContent = pad(values.seconds);
          ariaParts.push(`${values.seconds} seconds`);
        }

        this.setAttribute('aria-label', ariaParts.join(', '));
      }

      handleExpired() {
        if (this.intervalId) {
          window.clearInterval(this.intervalId);
          this.intervalId = null;
        }

        switch (this.expiredBehavior) {
          case 'show_message':
            this.replaceChildren(document.createTextNode(this.expiredMessage));
            break;

          case 'keep_message':
            this.hidden = true;
            break;

          case 'hide_slide':
          default:
            this.hideAnnouncement();
            break;
        }
      }

      hideAnnouncement() {
        const slide = this.closest('.slideshow__slide');
        const utilityBar = this.closest('.utility-bar');

        if (slide) {
          slide.hidden = true;
          slide.setAttribute('aria-hidden', 'true');
          this.hideEmptySlider(slide.closest('.slider'));
        } else if (utilityBar) {
          utilityBar.hidden = true;
        }

        this.hidden = true;
      }

      hideEmptySlider(slider) {
        if (!slider) return;

        const visibleSlides = slider.querySelectorAll('.slideshow__slide:not([hidden])');
        if (visibleSlides.length === 0) {
          const utilityBar = slider.closest('.utility-bar');
          if (utilityBar) utilityBar.hidden = true;
        }
      }
    },
  );
}
